// The one time a questionnaire link is visible: minted here, copied or
// mailed, then only its hash exists. Mounted only while open, so state
// is born fresh each time.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Dialog } from "../../../components/chrome/Dialog";
import { createIntakeLink, fetchIntakeTemplate } from "../api/intake";
import { IssuedLinkReveal } from "./IssuedLinkReveal";
import { languageName } from "../../../lib/intakeLanguages";

export function SendLinkDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [clientName, setClientName] = useState("");
  const [category, setCategory] = useState("");
  const [language, setLanguage] = useState("ru");
  const [email, setEmail] = useState("");

  const template = useQuery({ queryKey: ["intake", "template"], queryFn: fetchIntakeTemplate });

  const create = useMutation({
    mutationFn: createIntakeLink,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["intake", "sessions"] }),
  });

  const languages = template.data?.languages ?? [];
  const lang = languages.length > 0 && !languages.includes(language as never) ? "en" : language;

  const issued = create.data;
  return (
    <Dialog open title="Create an intake link" onClose={onClose} locked={create.isPending}>
      {issued ? (
        <div className="cc-form">
          <p className="intake-prose">
            This is the only time the link is shown - a resend mints a new one and kills this one.
          </p>
          <IssuedLinkReveal issue={issued} testId="issued-link" />
          <div className="cc-form-actions">
            <button type="button" className="cc-link-btn" onClick={onClose}>
              Done
            </button>
          </div>
        </div>
      ) : (
        <div className="cc-form">
          <label className="cc-form-field">
            <span className="cc-form-label">Client name</span>
            <input
              className="cc-field"
              aria-label="Link client name"
              maxLength={120}
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
            />
            <span className="cc-form-hint">Optional - shown to the office, not to the client.</span>
          </label>
          <label className="cc-form-field">
            <span className="cc-form-label">Case type</span>
            <select
              className="cc-field"
              aria-label="Link case type"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              <option value="">The client will choose</option>
              {(template.data?.template.categories ?? []).map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label.en}
                </option>
              ))}
            </select>
            <span className="cc-form-hint">
              Set here, the case type is the office's call - the client cannot change it.
            </span>
          </label>
          <label className="cc-form-field">
            <span className="cc-form-label">Client language</span>
            <select
              className="cc-field"
              aria-label="Link language"
              value={lang}
              onChange={(e) => setLanguage(e.target.value)}
            >
              {(languages.length > 0 ? languages : ["en"]).map((l) => (
                <option key={l} value={l}>
                  {languageName(l)}
                </option>
              ))}
            </select>
            <span className="cc-form-hint">
              The questionnaire opens in this language; the client can switch.
            </span>
          </label>
          <label className="cc-form-field">
            <span className="cc-form-label">Email the link to</span>
            <input
              className="cc-field"
              aria-label="Link email"
              type="email"
              maxLength={200}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <span className="cc-form-hint">
              Optional - leave empty to copy the link and send it by any channel.
            </span>
          </label>
          {template.isError && (
            <div role="alert" className="cc-form-error">
              {template.error.message}
            </div>
          )}
          {create.isError && (
            <div role="alert" className="cc-form-error">
              {create.error.message}
            </div>
          )}
          <div className="cc-form-actions">
            <button
              type="button"
              className="cc-btn-primary"
              disabled={create.isPending || !template.isSuccess}
              onClick={() =>
                create.mutate({
                  clientName: clientName.trim() || undefined,
                  category: category || undefined,
                  language: lang,
                  email: email.trim() || undefined,
                })
              }
            >
              {create.isPending ? "Creating..." : "Create the link"}
            </button>
          </div>
        </div>
      )}
    </Dialog>
  );
}
